/**
 * The iframe side of the dashboard ↔ help-widget protocol — a deliberate,
 * trimmed sibling of embed/bridge.ts (NOT a generalization of it: the comms
 * bridge carries comms-only relays and must not be destabilized). The Flutter
 * dashboard frames `/help-widget` where its Chatwoot bubble used to live.
 *
 *   iframe → parent  HELP_WIDGET_READY           mounted — send INIT now
 *   parent → iframe  HELP_WIDGET_INIT            { token, tenant, theme?, route? }
 *   iframe → parent  HELP_WIDGET_REQUEST_TOKEN   need a fresh ID token
 *   parent → iframe  HELP_WIDGET_TOKEN           { token }
 *   parent → iframe  HELP_WIDGET_SET_THEME       { theme: 'light' | 'dark' }
 *   parent → iframe  HELP_WIDGET_SET_ROUTE       { route } — the screen the operator is on
 *   parent → iframe  HELP_WIDGET_SET_PAGE        { page }  — what that screen publishes
 *                                                about its own state, for debugging
 *                                                questions ("why is this template
 *                                                wrong?"). Scalars only; the server
 *                                                re-checks the shape.
 *   iframe → parent  HELP_WIDGET_AUTH_FAILED     fresh token still bounced — re-login
 *   iframe → parent  HELP_WIDGET_CLOSE           the reader dismissed the panel
 *
 * Security model: identical to the comms embed. The iframe holds no session;
 * the first INIT pins the parent origin; tokens only flow parent → iframe and
 * never ride URLs.
 */

export interface HelpWidgetInit {
  tenant: string;
  theme: 'light' | 'dark';
  route: string | null;
}

/** Mirrors HelpPageSnapshot; kept structural so the bridge owns no UI types. */
export interface HelpWidgetPage {
  title?: string;
  entity?: string;
  id?: string | number;
  state?: Record<string, string | number | boolean | null | undefined>;
}

const TOKEN_TIMEOUT_MS = 10_000;

interface BridgeMessage {
  type: string;
  [key: string]: unknown;
}

const isBridgeMessage = (data: unknown): data is BridgeMessage =>
  typeof data === 'object' &&
  data !== null &&
  typeof (data as { type?: unknown }).type === 'string' &&
  (data as { type: string }).type.startsWith('HELP_WIDGET_');

export class HelpWidgetBridge {
  onInit: (init: HelpWidgetInit) => void = () => {};
  onTheme: (theme: 'light' | 'dark') => void = () => {};
  onRoute: (route: string | null) => void = () => {};
  onPage: (page: HelpWidgetPage | null) => void = () => {};

  private parentOrigin: string | null = null;
  private token: string | null = null;
  private waiters: { resolve: (token: string) => void; timer: ReturnType<typeof setTimeout> }[] =
    [];

  constructor(private readonly win: Window = window) {}

  start(): void {
    this.win.addEventListener('message', this.handleMessage);
    // No secrets in READY, so '*' is fine — the parent is unknown until INIT.
    this.post({ type: 'HELP_WIDGET_READY' }, '*');
  }

  dispose(): void {
    this.win.removeEventListener('message', this.handleMessage);
    for (const waiter of this.waiters) clearTimeout(waiter.timer);
    this.waiters = [];
  }

  /** Token source for the API client. */
  getToken = (forceFresh: boolean): Promise<string> => {
    if (!forceFresh && this.token) return Promise.resolve(this.token);
    this.post({ type: 'HELP_WIDGET_REQUEST_TOKEN' });
    return new Promise<string>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.waiters = this.waiters.filter((waiter) => waiter.timer !== timer);
        reject(new Error('The dashboard did not answer the token request.'));
      }, TOKEN_TIMEOUT_MS);
      this.waiters.push({ resolve, timer });
    });
  };

  notifyAuthFailed(): void {
    this.post({ type: 'HELP_WIDGET_AUTH_FAILED' });
  }

  /**
   * The panel's own close button. The host owns the container, so dismissing
   * is a request, not something the frame can do to itself.
   */
  requestClose(): void {
    this.post({ type: 'HELP_WIDGET_CLOSE' });
  }

  private handleMessage = (event: MessageEvent): void => {
    const data: unknown = event.data;
    if (!isBridgeMessage(data)) return;

    if (this.parentOrigin === null) {
      // The first accepted message must be INIT; it pins the parent origin.
      if (data.type !== 'HELP_WIDGET_INIT') return;
      this.parentOrigin = event.origin;
    } else if (event.origin !== this.parentOrigin) {
      return;
    }

    switch (data.type) {
      case 'HELP_WIDGET_INIT': {
        if (typeof data.token !== 'string' || typeof data.tenant !== 'string') return;
        this.acceptToken(data.token);
        this.onInit({
          tenant: data.tenant,
          theme: data.theme === 'dark' ? 'dark' : 'light',
          route: typeof data.route === 'string' ? data.route : null,
        });
        break;
      }
      case 'HELP_WIDGET_TOKEN': {
        if (typeof data.token !== 'string') return;
        this.acceptToken(data.token);
        break;
      }
      case 'HELP_WIDGET_SET_THEME': {
        this.onTheme(data.theme === 'dark' ? 'dark' : 'light');
        break;
      }
      case 'HELP_WIDGET_SET_ROUTE': {
        this.onRoute(typeof data.route === 'string' ? data.route : null);
        break;
      }
      case 'HELP_WIDGET_SET_PAGE': {
        const page = data.page;
        // Structurally checked only — the server sanitizes for real, and a
        // host that sends nonsense should lose the context, not the panel.
        this.onPage(page && typeof page === 'object' && !Array.isArray(page) ? (page as HelpWidgetPage) : null);
        break;
      }
      default:
        break;
    }
  };

  private acceptToken(token: string): void {
    this.token = token;
    const pending = this.waiters;
    this.waiters = [];
    for (const waiter of pending) {
      clearTimeout(waiter.timer);
      waiter.resolve(token);
    }
  }

  private post(message: BridgeMessage, origin?: string): void {
    try {
      this.win.parent.postMessage(message, origin ?? this.parentOrigin ?? '*');
    } catch {
      // A failed post must never crash the widget; the token timeout covers it.
    }
  }
}
